import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);      // null = checking, false = not-authed, obj = authed
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("ycn_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("ycn_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("ycn_token", data.token);
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (e) {
      return { success: false, error: formatApiError(e) };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("ycn_token");
    setUser(false);
  }, []);

  const changePassword = useCallback(async (current_password, new_password) => {
    try {
      await api.post("/auth/change-password", { current_password, new_password });
      // Refresh user so needs_password_change flips to false
      const { data } = await api.get("/auth/me");
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiError(e) };
    }
  }, []);

  // Memoize the context value so consumers don't re-render on every AuthProvider render.
  const value = useMemo(
    () => ({ user, loading, login, logout, changePassword, refresh: fetchMe }),
    [user, loading, login, logout, changePassword, fetchMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
